import { paths } from 'src/routes/paths';

import { TOUR_ANCHORS, navAnchor } from '../tour-anchors';
import type { TourDefinition } from '../types';

// Module-level const: the `tours` prop passed to TourProvider must keep a
// stable reference across renders, or the polling effect (keyed off the step
// object) re-runs on every render.
export const APP_TOUR: TourDefinition = {
  id: 'app-tour',
  version: 3,
  steps: [
    {
      id: 'intro',
      anchor: null,
      route: paths.dailyOverview.root,
      title: "You're connected — here's how this works",
      body: "Athory is already watching your stock and building demand models. You don't run reports here — Athory tells you when something needs doing. Two minutes, and you'll know the whole loop.",
    },
    {
      id: 'sidebar',
      anchor: TOUR_ANCHORS.sidebarNav,
      title: 'The map',
      body: "Dashboards to check in, Inventory to track orders and stock movements, Management for your catalog and suppliers, Automation for what Athory suggests. You'll live mostly in two places — this tour shows you which.",
      placement: 'right',
    },
    {
      id: 'daily-overview',
      anchor: TOUR_ANCHORS.dailyOverviewPage,
      route: paths.dailyOverview.root,
      title: 'Your morning check-in',
      body: "Once a day, scan this page: what needs attention, what's arriving, and how fulfillment is going. Most days that's all Athory asks of you.",
    },
    {
      id: 'activation-ladder',
      anchor: TOUR_ANCHORS.dailyOverviewActivationLadder,
      route: paths.dailyOverview.root,
      // The ladder card retires itself at the top activation level; skip the
      // step instead of teaching an invisible card from the centered fallback.
      skipIfAbsent: true,
      title: 'Your progress meter',
      body: 'Athory unlocks smarter ordering in levels. Level 2 arrives on its own once your order history finishes importing. Levels 3 and 4 come from three actions — assign suppliers, record deliveries, add costs — and this tour shows you where each one lives.',
      placement: 'top',
    },
    {
      id: 'recommendations-nav',
      anchor: navAnchor(paths.recommendations.root),
      title: 'When Athory needs you, it shows up here',
      body: "The badge counts suggestions waiting for review. Your first reorder suggestions arrive within about a day — we'll email you when they're live.",
      placement: 'right',
    },
    {
      id: 'recommendations-list',
      anchor: TOUR_ANCHORS.recommendationsList,
      route: paths.recommendations.root,
      title: 'Four kinds of suggestions, ranked by urgency',
      body: 'Reorder from a supplier, assign a supplier, verify inventory counts, reduce excess stock. Urgent ones rise to the top with an order-by date. Snooze or dismiss anything — Athory keeps score honestly.',
      placement: 'top',
      demo: true,
      illustration: 'recommendation',
    },
    {
      id: 'review-to-po',
      anchor: null,
      route: paths.recommendations.root,
      // Adaptive: with a live reorder suggestion on the list, visit its real
      // Review-PO page and spotlight the budget strip; fresh accounts (no
      // recommendations yet) keep the illustrated card below.
      detail: {
        source: TOUR_ANCHORS.recommendationsList,
        anchor: TOUR_ANCHORS.recommendationBudget,
      },
      title: 'Two clicks from suggestion to order',
      body: 'Opening a reorder suggestion shows the full plan — quantities, costs, the order-by date, and why that many. Set a budget cap and Athory re-plans the order to fit it. One click turns it into a draft purchase order with every line filled in.',
      placement: 'bottom',
      demo: true,
      illustration: 'planToDraft',
    },
    {
      id: 'purchase-orders-list',
      anchor: TOUR_ANCHORS.purchaseOrdersList,
      route: paths.purchaseorders.root,
      title: 'Track every order from draft to received',
      body: 'Draft purchase orders land here. Send the order to your supplier the way you always do — then track its status and expected arrival in this list.',
      placement: 'top',
      illustration: 'purchaseOrder',
    },
    {
      id: 'receiving-learns',
      anchor: null,
      route: paths.purchaseorders.root,
      title: 'Receiving is how Athory learns',
      body: "When a delivery arrives, hit Receive Items and record the date. After three deliveries, Athory replaces a supplier's quoted lead time with the measured one in your reorder math. This is the single habit that makes everything sharper.",
      illustration: 'poDetail',
    },
    {
      id: 'products-list',
      anchor: TOUR_ANCHORS.productsList,
      route: paths.products.list,
      title: 'Your catalog — and two setup actions',
      body: "Every tracked product lives here. Two things unlock Athory's full intelligence: assign each product a supplier, and add unit costs. The Needs Setup filter shows exactly what's left.",
      placement: 'top',
    },
    {
      id: 'product-detail',
      anchor: null,
      route: paths.products.list,
      // Adaptive: with a tracked product on the list, visit its real detail
      // page and spotlight the forecast/projection charts; the illustrated
      // card remains the fallback for empty catalogs.
      detail: {
        source: TOUR_ANCHORS.productsList,
        anchor: TOUR_ANCHORS.productDetailForecast,
      },
      title: 'Every product tells you its story',
      body: 'Open any product: its demand forecast, projected stock with incoming orders marked, and its supplier links — including the lead time Athory has learned versus what was quoted.',
      placement: 'top',
      // Deliberately NOT demo-backed. This page is genuinely the merchant's, so
      // a demo forecast would render a fictional demand curve beside their real
      // SKU, real on-hand and real suppliers — the chart derives its stock from
      // live inventory, so the two cannot be told apart in a screenshot. The
      // illustrated card keeps the tutorial/live boundary intact.
      illustration: 'forecastProjection',
    },
    {
      id: 'supplier-performance',
      anchor: TOUR_ANCHORS.suppliersPerformance,
      route: paths.suppliers.performance,
      title: 'Suppliers, measured — not promised',
      body: 'After a few received deliveries, this page shows how suppliers actually perform: real lead times, reliability, and where your spend is exposed. Those measurements feed straight into your safety stock.',
      placement: 'top',
    },
    {
      id: 'inventory-health',
      anchor: TOUR_ANCHORS.inventoryHealthPage,
      route: paths.inventoryHealth.root,
      title: 'Zoom out',
      body: 'How your whole inventory splits across health tiers, how long it covers demand, and where capital is tied up. Daily Overview is for today; this page is for the bigger picture.',
    },
    {
      id: 'finish',
      anchor: null,
      route: paths.dailyOverview.root,
      title: 'The rhythm',
      body: "Daily: scan the overview. Weekly: act on recommendations and receive deliveries. Once: assign suppliers and add costs. Your first recommendations arrive within about a day — we'll email you. Restart this tour anytime from the user menu.",
      illustration: 'rhythm',
    },
  ],
};
